import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Pencil, Search, Link2, BookOpen, Wallet, UserPlus, FileSignature, Upload, Download, LogOut, ArrowRightLeft, Users, FileText, Mail } from "lucide-react";
import { api, apiError, contratoBlobUrl, descargarContratoPdf } from "@/lib/api";
import { norm, money, fecha } from "@/lib/format";
import { usePermisos } from "@/lib/permisos";
import { Loading, PageTitle } from "@/components/Common";
import { FichaEstudiante } from "@/components/FichaEstudiante";
import { SelectorVista, VistaEstudiantes, useVistaEstudiantes } from "@/components/VistasEstudiantes";
import { DialogoSalida, DialogoTraslado, ListaSalidas, descargarConstancia, enviarConstancia } from "@/components/SalidasEstudiantes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const vacio = {
  tutor_id: "", nombre: "", matricula: "", grado: "5to Primaria", seccion: "A",
  fecha_nacimiento: "", estado: "activo", datos_medicos: "", alergias: "",
  contacto_emergencia: "", diagnostico: "",
};
const tutorVacio = { nombre: "", email: "", password: "", telefono: "", direccion: "" };

const GRADOS_SUGERIDOS = [
  "Pre-Kínder (3 años)", "Kínder (4 años)", "Pre-primario (5 años)",
  "1ro Primaria", "2do Primaria", "3ro Primaria", "4to Primaria", "5to Primaria", "6to Primaria",
  "1ro Secundaria", "2do Secundaria", "3ro Secundaria", "4to Secundaria", "5to Secundaria",
  "6to Secundaria", "Sin asignar",
];

export default function AdminEstudiantes() {
  const [rows, setRows] = useState(null);
  const [tutores, setTutores] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [combos, setCombos] = useState([]);
  const [actividades, setActividades] = useState([]);
  const [form, setForm] = useState(null);
  const [tutorNuevo, setTutorNuevo] = useState(null);
  const [asig, setAsig] = useState(null);
  const [contrato, setContrato] = useState(null);
  const [fichaId, setFichaId] = useState(null);
  const [contratoPdf, setContratoPdf] = useState(null);
  const contratoFileRef = useRef();
  const [busqueda, setBusqueda] = useState("");
  const [filtroGrado, setFiltroGrado] = useState("todos");
  const [pestana, setPestana] = useState("activos");
  const [salida, setSalida] = useState(null);
  const [traslado, setTraslado] = useState(null);
  const { vista, agrupado, cambiarVista, cambiarAgrupado } = useVistaEstudiantes();
  const { puede } = usePermisos();

  const accionesEstudiante = (e) => (
    <>
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setForm(e); setTutorNuevo(null); }}
              data-testid={`editar-estudiante-${e.matricula}`}>
        <Pencil className="mr-1 h-3 w-3" /> Editar
      </Button>
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => abrirAsignaciones(e)}
              data-testid={`asignaciones-${e.matricula}`}>
        <Link2 className="mr-1 h-3 w-3" /> Asignaciones
      </Button>
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => abrirContrato(e)}
              data-testid={`contrato-${e.matricula}`}>
        <FileSignature className="mr-1 h-3 w-3" /> Contrato
      </Button>
      <Button size="sm" variant="outline" className="rounded-full"
              onClick={() => descargarConstancia(e).catch(() => toast.error("No se pudo generar la constancia"))}
              title="Descargar la constancia de estudios"
              data-testid={`constancia-${e.matricula}`}>
        <FileText className="mr-1 h-3 w-3" /> Constancia
      </Button>
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => enviarConstancia(e)}
              title="Enviar la constancia de estudios por correo a la familia"
              data-testid={`enviar-constancia-${e.matricula}`}>
        <Mail className="mr-1 h-3 w-3" /> Enviar
      </Button>
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setTraslado(e)}
              data-testid={`traslado-${e.matricula}`}>
        <ArrowRightLeft className="mr-1 h-3 w-3" /> Mover de grado
      </Button>
      {puede("estudiantes:eliminar") && (
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setSalida(e)}
                data-testid={`retirar-${e.matricula}`}>
          <LogOut className="mr-1 h-3 w-3" /> Retirar
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={() => eliminar(e.id)}
              data-testid={`eliminar-estudiante-${e.matricula}`}>
        <Trash2 className="h-3 w-3 text-destructive" />
      </Button>
    </>
  );

  const cargar = () => {
    api.get("/estudiantes").then((r) => setRows(r.data));
    api.get("/tutores").then((r) => setTutores(r.data));
    api.get("/planes-pago").then((r) => setPlanes(r.data));
    api.get("/combos").then((r) => setCombos(r.data));
    api.get("/actividades").then((r) => setActividades(r.data));
  };

  useEffect(cargar, []);

  const guardar = async () => {
    if (!form.nombre.trim()) return toast.error("Indique el nombre del estudiante");
    if (!form.matricula.trim()) return toast.error("Indique la matrícula");
    const payload = { ...form };
    ["id", "creado_en", "tutor_nombre", "materia_ids"].forEach((k) => delete payload[k]);
    try {
      if (form.id) {
        await api.put(`/estudiantes/${form.id}`, payload);
        toast.success("Ficha actualizada");
      } else if (tutorNuevo) {
        if (!tutorNuevo.nombre.trim() || !tutorNuevo.email.trim()) {
          return toast.error("Complete el nombre y correo del tutor");
        }
        if (tutorNuevo.password.length < 6) return toast.error("La contraseña del tutor debe tener 6+ caracteres");
        const { data } = await api.post("/estudiantes-con-tutor", {
          estudiante: { ...payload, tutor_id: null }, tutor_nuevo: tutorNuevo,
        });
        toast.success(`Estudiante y tutor ${data.tutor.nombre} registrados`);
      } else {
        if (!payload.tutor_id) return toast.error("Seleccione el tutor o registre uno nuevo");
        await api.post("/estudiantes-con-tutor", { estudiante: payload });
        toast.success("Estudiante registrado");
      }
      setForm(null);
      setTutorNuevo(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const eliminar = async (id) => {
    await api.delete(`/estudiantes/${id}`);
    toast.success("Estudiante eliminado");
    cargar();
  };

  const abrirAsignaciones = async (e) => {
    const { data } = await api.get(`/estudiantes/${e.id}/asignaciones`);
    setAsig({ ...data, estudianteRef: e, generar: true });
  };

  const guardarMaterias = async () => {
    await api.put(`/estudiantes/${asig.estudiante.id}/materias`, { materia_ids: asig.materia_ids });
    toast.success("Materias asignadas");
  };

  const guardarPlan = async (planId) => {
    try {
      const { data } = await api.put(`/estudiantes/${asig.estudiante.id}/plan-pago`, {
        plan_pago_id: planId || null, generar_facturas: asig.generar,
      });
      toast.success(data.facturas_generadas?.length
        ? `Modalidad asignada y ${data.facturas_generadas.length} factura(s) generada(s)`
        : "Modalidad asignada");
      const plan = planes.find((p) => p.id === planId) || null;
      setAsig({ ...asig, plan_pago: plan });
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleCombo = async (cid, activo) => {
    try {
      if (activo) {
        await api.delete(`/estudiantes/${asig.estudiante.id}/combos/${cid}`);
        setAsig({ ...asig, combo_ids: asig.combo_ids.filter((x) => x !== cid) });
        toast.success("Combo retirado");
      } else {
        const { data } = await api.post(`/estudiantes/${asig.estudiante.id}/combos-admin/${cid}`);
        setAsig({ ...asig, combo_ids: [...asig.combo_ids, cid] });
        toast.success(`Combo asignado · factura ${data.factura.ncf}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleActividad = async (aid, activo) => {
    try {
      if (activo) {
        await api.delete(`/estudiantes/${asig.estudiante.id}/actividades/${aid}`);
        setAsig({ ...asig, actividad_ids: asig.actividad_ids.filter((x) => x !== aid) });
        toast.success("Actividad retirada");
      } else {
        const { data } = await api.post(`/estudiantes/${asig.estudiante.id}/actividades/${aid}`);
        setAsig({ ...asig, actividad_ids: [...asig.actividad_ids, aid] });
        toast.success(data.factura ? `Actividad asignada · factura ${data.factura.ncf}` : "Actividad asignada");
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const abrirContrato = async (e) => {
    setContrato({ estudiante: e, datos: null });
    setContratoPdf(null);
    const { data } = await api.get(`/estudiantes/${e.id}/contrato`);
    setContrato({ estudiante: e, datos: data });
  };

  const subirContrato = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") return toast.error("El contrato debe ser un archivo PDF");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/estudiantes/${contrato.estudiante.id}/contrato/pdf`, {
          nombre: file.name, archivo_base64: String(reader.result),
        });
        toast.success("Contrato en PDF subido a la ficha");
        abrirContrato(contrato.estudiante);
      } catch (e) {
        toast.error(apiError(e));
      }
    };
    reader.readAsDataURL(file);
  };

  const verContrato = async () => {
    setContratoPdf("cargando");
    try {
      setContratoPdf(await contratoBlobUrl(contrato.estudiante.id));
    } catch (e) {
      setContratoPdf(null);
      toast.error(apiError(e, "No se pudo abrir el contrato"));
    }
  };

  const quitarContrato = async () => {
    try {
      await api.delete(`/estudiantes/${contrato.estudiante.id}/contrato/pdf`);
      toast.success("Contrato en PDF eliminado");
      setContratoPdf(null);
      abrirContrato(contrato.estudiante);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!rows) return <Loading />;

  const FUERA = ["retirado", "transferido", "egresado"];
  const activos = rows.filter((e) => !FUERA.includes(e.estado));
  const salidas = rows.filter((e) => FUERA.includes(e.estado));
  const q = norm(busqueda.trim());
  const coincide = (e) => (!q || [e.nombre, e.matricula, e.tutor_nombre, e.grado, e.seccion]
    .filter(Boolean).some((v) => norm(v).includes(q)));
  const visibles = activos.filter(
    (e) => (filtroGrado === "todos" || e.grado === filtroGrado) && coincide(e));
  const salidasVisibles = salidas.filter(coincide);
  const grados = [...new Set(rows.map((e) => e.grado))];
  const sinGrado = activos.filter((e) => e.grado === "Sin asignar").length;

  return (
    <div data-testid="admin-estudiantes">
      <PageTitle title="Fichas de estudiantes"
                 subtitle="Datos personales y médicos, tutor responsable, materias, combos, actividades y modalidad de pago.">
        <Button className="rounded-full" onClick={() => { setForm({ ...vacio, tutor_id: tutores[0]?.id || "" }); setTutorNuevo(null); }}
                data-testid="nuevo-estudiante">
          <Plus className="mr-2 h-4 w-4" /> Nuevo estudiante
        </Button>
      </PageTitle>

      <div className="mb-6 flex flex-wrap gap-2 border-b border-border pb-3">
        <button onClick={() => setPestana("activos")} data-testid="pestana-activos"
                className={`rounded-full px-5 py-2 text-sm lift ${
                  pestana === "activos" ? "bg-primary font-semibold text-primary-foreground"
                                        : "text-muted-foreground hover:bg-muted"}`}>
          <Users className="mr-2 inline h-4 w-4" /> Activos ({activos.length})
        </button>
        <button onClick={() => setPestana("salidas")} data-testid="pestana-salidas"
                className={`rounded-full px-5 py-2 text-sm lift ${
                  pestana === "salidas" ? "bg-primary font-semibold text-primary-foreground"
                                        : "text-muted-foreground hover:bg-muted"}`}>
          <LogOut className="mr-2 inline h-4 w-4" /> Retirados y transferidos ({salidas.length})
        </button>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="rounded-full pl-10" placeholder="Buscar por nombre, matrícula o tutor…"
                 value={busqueda} onChange={(e) => setBusqueda(e.target.value)} data-testid="buscar-estudiante" />
        </div>
        {pestana === "activos" && (
          <select value={filtroGrado} onChange={(e) => setFiltroGrado(e.target.value)} data-testid="filtro-grado"
                  className="rounded-full border border-border bg-card px-4 py-2 text-sm">
            <option value="todos">Todos los grados</option>
            {grados.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        )}
        <span className="text-xs text-muted-foreground" data-testid="contador-estudiantes">
          {pestana === "activos" ? `${visibles.length} de ${activos.length}`
                                 : `${salidasVisibles.length} de ${salidas.length}`}
        </span>
        {pestana === "activos" && sinGrado > 0 && (
          <Button size="sm" variant={filtroGrado === "Sin asignar" ? "default" : "outline"}
                  className="rounded-full" data-testid="filtro-sin-grado"
                  onClick={() => setFiltroGrado(filtroGrado === "Sin asignar" ? "todos" : "Sin asignar")}>
            Sin grado ({sinGrado})
          </Button>
        )}
        {pestana === "activos" && (
          <SelectorVista vista={vista} agrupado={agrupado} onVista={cambiarVista}
                         onAgrupado={cambiarAgrupado} />
        )}
      </div>

      {pestana === "salidas" ? (
        <ListaSalidas estudiantes={salidasVisibles} onListo={cargar} onFicha={setFichaId} />
      ) : (
        <VistaEstudiantes estudiantes={visibles} planes={planes} vista={vista} agrupado={agrupado}
                          onFicha={setFichaId} acciones={accionesEstudiante}
                          vacio={activos.length === 0 ? "Aún no hay estudiantes activos."
                                                       : "Ningún estudiante coincide con la búsqueda."} />
      )}

      <DialogoSalida estudiante={salida} onClose={() => setSalida(null)} onListo={cargar} />
      <DialogoTraslado estudiante={traslado} grados={GRADOS_SUGERIDOS} onListo={cargar}
                       onClose={() => setTraslado(null)} />

      <FichaEstudiante estudianteId={fichaId} onClose={() => setFichaId(null)} />

      <Dialog open={!!form} onOpenChange={(o) => { if (!o) { setForm(null); setTutorNuevo(null); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="modal-estudiante">
          <DialogHeader>
            <DialogTitle className="font-display">{form?.id ? "Editar ficha" : "Nuevo estudiante"}</DialogTitle>
            <DialogDescription>
              Puede registrar al padre o tutor en el mismo paso.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Nombre completo</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                       data-testid="form-nombre" />
              </div>
              <div className="space-y-2">
                <Label>Matrícula</Label>
                <Input value={form.matricula} onChange={(e) => setForm({ ...form, matricula: e.target.value })}
                       data-testid="form-matricula" />
              </div>
              <div className="space-y-2">
                <Label>Fecha de nacimiento</Label>
                <Input type="date" value={form.fecha_nacimiento || ""}
                       onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Grado</Label>
                <input list="lista-grados" value={form.grado} data-testid="form-grado"
                       onChange={(e) => setForm({ ...form, grado: e.target.value })}
                       className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" />
                <datalist id="lista-grados">
                  {GRADOS_SUGERIDOS.map((g) => <option key={g} value={g} />)}
                </datalist>
              </div>
              <div className="space-y-2">
                <Label>Sección</Label>
                <Input value={form.seccion || ""} onChange={(e) => setForm({ ...form, seccion: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Datos médicos</Label>
                <Input value={form.datos_medicos || ""}
                       onChange={(e) => setForm({ ...form, datos_medicos: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Alergias</Label>
                <Input value={form.alergias || ""} onChange={(e) => setForm({ ...form, alergias: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Diagnóstico (si aplica)</Label>
                <Input value={form.diagnostico || ""} data-testid="form-diagnostico"
                       onChange={(e) => setForm({ ...form, diagnostico: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Contacto de emergencia</Label>
                <Input value={form.contacto_emergencia || ""}
                       onChange={(e) => setForm({ ...form, contacto_emergencia: e.target.value })} />
              </div>

              {!form.id && (
                <div className="sm:col-span-2 rounded-lg border border-border bg-muted/30 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm font-semibold">Padre / tutor responsable</p>
                    <Button size="sm" variant={tutorNuevo ? "default" : "outline"} className="rounded-full"
                            data-testid="toggle-tutor-nuevo"
                            onClick={() => setTutorNuevo(tutorNuevo ? null : { ...tutorVacio })}>
                      <UserPlus className="mr-1 h-3 w-3" /> {tutorNuevo ? "Usar tutor existente" : "Registrar tutor nuevo"}
                    </Button>
                  </div>

                  {tutorNuevo ? (
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2 sm:col-span-2">
                        <Label>Nombre del tutor</Label>
                        <Input value={tutorNuevo.nombre} data-testid="tutor-nombre"
                               onChange={(e) => setTutorNuevo({ ...tutorNuevo, nombre: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Correo</Label>
                        <Input type="email" value={tutorNuevo.email} data-testid="tutor-email"
                               onChange={(e) => setTutorNuevo({ ...tutorNuevo, email: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Contraseña</Label>
                        <Input value={tutorNuevo.password} data-testid="tutor-password"
                               onChange={(e) => setTutorNuevo({ ...tutorNuevo, password: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Teléfono</Label>
                        <Input value={tutorNuevo.telefono} data-testid="tutor-telefono"
                               onChange={(e) => setTutorNuevo({ ...tutorNuevo, telefono: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Dirección</Label>
                        <Input value={tutorNuevo.direccion} data-testid="tutor-direccion"
                               onChange={(e) => setTutorNuevo({ ...tutorNuevo, direccion: e.target.value })} />
                      </div>
                    </div>
                  ) : (
                    <select className="mt-4 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                            value={form.tutor_id || ""} data-testid="form-tutor"
                            onChange={(e) => setForm({ ...form, tutor_id: e.target.value })}>
                      <option value="">Seleccione un tutor…</option>
                      {tutores.map((t) => <option key={t.id} value={t.id}>{`${t.nombre} — ${t.email}`}</option>)}
                    </select>
                  )}
                </div>
              )}

              {form.id && (
                <div className="space-y-2 sm:col-span-2">
                  <Label>Tutor responsable</Label>
                  <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                          value={form.tutor_id || ""} data-testid="form-tutor"
                          onChange={(e) => setForm({ ...form, tutor_id: e.target.value })}>
                    <option value="">Sin tutor</option>
                    {tutores.map((t) => <option key={t.id} value={t.id}>{`${t.nombre} — ${t.email}`}</option>)}
                  </select>
                </div>
              )}

              <Button className="sm:col-span-2 rounded-full py-6" onClick={guardar} data-testid="guardar-estudiante">
                Guardar ficha
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!contrato} onOpenChange={(o) => { if (!o) { setContrato(null); setContratoPdf(null); } }}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="modal-contrato-admin">
          <DialogHeader>
            <DialogTitle className="font-display">Contrato de {contrato?.estudiante?.nombre}</DialogTitle>
            <DialogDescription>
              Suba el contrato firmado en PDF a la ficha del estudiante. El tutor podrá verlo y descargarlo en su portal.
            </DialogDescription>
          </DialogHeader>
          {!contrato?.datos ? <Loading label="Cargando contrato…" /> : (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant={contrato.datos.firmado ? "secondary" : "outline"} data-testid="contrato-estado-firma">
                  {contrato.datos.firmado ? `Firmado por ${contrato.datos.firmado_por}` : "Sin firma electrónica"}
                </Badge>
                <Badge variant={contrato.datos.pdf_nombre ? "default" : "outline"} data-testid="contrato-estado-pdf">
                  {contrato.datos.pdf_nombre ? "PDF cargado" : "Sin PDF"}
                </Badge>
                <span className="text-xs text-muted-foreground">Año lectivo {contrato.datos.anio_lectivo}</span>
              </div>

              <input ref={contratoFileRef} type="file" accept="application/pdf" className="hidden"
                     onChange={subirContrato} data-testid="contrato-file-input" />

              <div className="flex flex-wrap gap-2">
                <Button className="rounded-full" onClick={() => contratoFileRef.current?.click()}
                        data-testid="subir-contrato-pdf">
                  <Upload className="mr-2 h-4 w-4" />
                  {contrato.datos.pdf_nombre ? "Reemplazar PDF" : "Subir contrato en PDF"}
                </Button>
                {contrato.datos.pdf_nombre && (
                  <>
                    <Button variant="outline" className="rounded-full" onClick={verContrato}
                            data-testid="ver-contrato-admin">Ver PDF</Button>
                    <Button variant="outline" className="rounded-full"
                            onClick={() => descargarContratoPdf(contrato.estudiante.id, contrato.datos.pdf_nombre)}
                            data-testid="descargar-contrato-admin">
                      <Download className="mr-2 h-4 w-4" /> Descargar
                    </Button>
                    <Button variant="ghost" onClick={quitarContrato} data-testid="quitar-contrato-pdf">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </>
                )}
              </div>

              {contrato.datos.pdf_nombre && (
                <p className="text-xs text-muted-foreground" data-testid="contrato-pdf-meta">
                  {contrato.datos.pdf_nombre} · subido el {fecha(contrato.datos.pdf_subido_en)} por {contrato.datos.pdf_subido_por}
                </p>
              )}

              {contratoPdf && contratoPdf !== "cargando" && (
                <iframe src={contratoPdf} title="Contrato" data-testid="visor-contrato-admin"
                        className="h-[60vh] w-full rounded-md border border-border" />
              )}
              {contratoPdf === "cargando" && (
                <div className="flex h-[60vh] items-center justify-center text-sm text-muted-foreground">
                  Cargando contrato…
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!asig} onOpenChange={(o) => !o && setAsig(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="modal-asignaciones">
          <DialogHeader>
            <DialogTitle className="font-display">Asignaciones de {asig?.estudiante?.nombre}</DialogTitle>
            <DialogDescription>Materias, combos, actividades y modalidad de pago.</DialogDescription>
          </DialogHeader>
          {asig && (
            <div className="space-y-8">
              <section>
                <div className="flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-primary" />
                  <h3 className="font-display text-base font-semibold">Modalidad de pago</h3>
                </div>
                <select className="mt-3 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={asig.plan_pago?.id || ""} data-testid="asig-plan"
                        onChange={(e) => guardarPlan(e.target.value)}>
                  <option value="">Sin modalidad asignada</option>
                  {planes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {`${p.nombre} — ${money(p.monto_base * (1 - (p.descuento_pct || 0) / 100))}${p.cuotas > 1 ? ` en ${p.cuotas} cuotas` : ""}`}
                    </option>
                  ))}
                </select>
                <label className="mt-3 flex items-center gap-3 text-sm">
                  <Checkbox checked={asig.generar} data-testid="asig-generar-facturas"
                            onCheckedChange={(v) => setAsig({ ...asig, generar: !!v })} />
                  Generar automáticamente las facturas de las cuotas al asignar
                </label>
              </section>

              <section>
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-primary" />
                  <h3 className="font-display text-base font-semibold">Materias</h3>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {asig.materias_disponibles.map((m) => (
                    <label key={m.id} className="flex items-center gap-3 rounded-md border border-border/60 p-3 text-sm">
                      <Checkbox checked={asig.materia_ids.includes(m.id)} data-testid={`asig-materia-${m.id}`}
                                onCheckedChange={(v) => setAsig({
                                  ...asig,
                                  materia_ids: v ? [...asig.materia_ids, m.id] : asig.materia_ids.filter((x) => x !== m.id),
                                })} />
                      <span className="min-w-0">
                        <span className="block truncate">{m.nombre}</span>
                        <span className="text-xs text-muted-foreground">{m.grado}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <Button size="sm" className="mt-4 rounded-full" onClick={guardarMaterias} data-testid="guardar-materias">
                  Guardar materias
                </Button>
              </section>

              <section>
                <h3 className="font-display text-base font-semibold">Combos</h3>
                <div className="mt-3 space-y-2">
                  {combos.map((c) => {
                    const activo = asig.combo_ids.includes(c.id);
                    return (
                      <div key={c.id} className="flex items-center justify-between gap-3 rounded-md border border-border/60 p-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{c.nombre}</p>
                          <p className="text-xs text-muted-foreground">{money(c.precio_total)}</p>
                        </div>
                        <Button size="sm" variant={activo ? "outline" : "default"} className="rounded-full"
                                onClick={() => toggleCombo(c.id, activo)} data-testid={`asig-combo-${c.id}`}>
                          {activo ? "Retirar" : "Asignar"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </section>

              <section>
                <h3 className="font-display text-base font-semibold">Actividades individuales</h3>
                <div className="mt-3 space-y-2">
                  {actividades.map((a) => {
                    const activo = asig.actividad_ids.includes(a.id);
                    return (
                      <div key={a.id} className="flex items-center justify-between gap-3 rounded-md border border-border/60 p-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{a.nombre}</p>
                          <p className="text-xs text-muted-foreground">{money(a.costo)}</p>
                        </div>
                        <Button size="sm" variant={activo ? "outline" : "default"} className="rounded-full"
                                onClick={() => toggleActividad(a.id, activo)} data-testid={`asig-actividad-${a.id}`}>
                          {activo ? "Retirar" : "Asignar"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
